/**
 * Inventory Service (Web)
 * Handles weapon inventory management with immediate API sync
 */

import { API_CONFIG } from '../config/api';
import { InventoryItem } from '../types';
import { authService } from './authService';

class InventoryService {
  /**
   * Get all inventory items for the current user
   */
  async getInventory(): Promise<InventoryItem[]> {
    try {
      const response = await authService.makeAuthenticatedRequest(
        `${API_CONFIG.baseUrl}/api/inventory`,
        { method: 'GET' }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch inventory: ${response.status}`);
      }

      const data = await response.json();
      return data.inventory || [];
    } catch (error) {
      console.error('[InventoryService] Error fetching inventory:', error);
      throw error;
    }
  }

  /**
   * Create a new inventory item
   */
  async createItem(item: Omit<InventoryItem, 'id' | 'userId' | 'lastModified' | 'syncVersion'>): Promise<InventoryItem> {
    try {
      const response = await authService.makeAuthenticatedRequest(
        `${API_CONFIG.baseUrl}/api/inventory`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(item),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to create item: ${response.status}`);
      }

      const data = await response.json();
      return data.item;
    } catch (error) {
      console.error('[InventoryService] Error creating item:', error);
      throw error;
    }
  }

  /**
   * Update an existing inventory item
   */
  async updateItem(id: string, updates: Partial<InventoryItem>): Promise<InventoryItem> {
    try {
      const response = await authService.makeAuthenticatedRequest(
        `${API_CONFIG.baseUrl}/api/inventory/${id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(updates),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to update item: ${response.status}`);
      }

      const data = await response.json();
      return data.item;
    } catch (error) {
      console.error('[InventoryService] Error updating item:', error);
      throw error;
    }
  }

  /**
   * Delete an inventory item
   */
  async deleteItem(id: string): Promise<void> {
    try {
      const response = await authService.makeAuthenticatedRequest(
        `${API_CONFIG.baseUrl}/api/inventory/${id}`,
        { method: 'DELETE' }
      );

      if (!response.ok) {
        throw new Error(`Failed to delete item: ${response.status}`);
      }
    } catch (error) {
      console.error('[InventoryService] Error deleting item:', error);
      throw error;
    }
  }

  /**
   * Set an item as the default
   */
  async setDefaultItem(id: string): Promise<InventoryItem> {
    try {
      const response = await authService.makeAuthenticatedRequest(
        `${API_CONFIG.baseUrl}/api/inventory/${id}/default`,
        { method: 'PUT' }
      );

      if (!response.ok) {
        throw new Error(`Failed to set default: ${response.status}`);
      }

      const data = await response.json();
      return data.item;
    } catch (error) {
      console.error('[InventoryService] Error setting default:', error);
      throw error;
    }
  }

  /**
   * Get the default inventory item
   */
  async getDefaultItem(): Promise<InventoryItem | null> {
    try {
      const items = await this.getInventory();
      return items.find(item => item.isDefault) || null;
    } catch (error) {
      console.error('[InventoryService] Error getting default item:', error);
      return null;
    }
  }
}

export const inventoryService = new InventoryService();
